import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Award,
  Sparkles,
  CheckCircle,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import type { Employee, ReviewData } from '../index';

interface SurfedStrengthsSkillsPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  onStrengthsValidationUpdate?: (validation: Record<string, unknown>) => void;
}

interface ReviewItem {
  id: string;
  name: string;
  type: 'skill';
  proficiency: string;
  endorsements: number;
  status: string | null;
}

interface StatusOption {
  value: string;
  label: string;
  Icon: typeof CheckCircle;
  color: string;
  bgColor: string;
  borderColor: string;
}

const SurfedStrengthsSkillsPanel = ({
  employee,
  reviewData,
  onStrengthsValidationUpdate,
}: SurfedStrengthsSkillsPanelProps) => {
  const { t } = useTranslation();

  // Parse real strengths from snapshot data
  const initialItems = (): ReviewItem[] => {
    try {
      if (!reviewData.snapshot?.strengthsSummary) return [];
      const data = JSON.parse(reviewData.snapshot.strengthsSummary);
      const skills = data.topSkills || [];
      return skills.map(
        (
          skill: {
            skillName: string;
            proficiency: string;
            endorsementCount: number;
          },
          idx: number,
        ) => ({
          id: `skill_${idx}`,
          name: skill.skillName,
          type: 'skill' as const,
          proficiency: skill.proficiency,
          endorsements: skill.endorsementCount,
          status: null as string | null,
        }),
      );
    } catch {
      return [];
    }
  };

  // Load saved validations if they exist
  const loadSavedValidations = (items: ReviewItem[]): ReviewItem[] => {
    try {
      if (!reviewData.snapshot?.strengthsValidation) return items;
      const saved = JSON.parse(reviewData.snapshot.strengthsValidation);
      const validations = saved.validations || [];
      return items.map((item) => {
        const match = validations.find(
          (v: { name: string }) => v.name === item.name,
        );
        return match ? { ...item, status: match.status } : item;
      });
    } catch {
      return items;
    }
  };

  const [items, setItems] = useState<ReviewItem[]>(() =>
    loadSavedValidations(initialItems()),
  );

  const statusOptions: StatusOption[] = [
    {
      value: 'accurate',
      label: t('performanceSnapshot.strengthsSkills.status.accurate'),
      Icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-600',
    },
    {
      value: 'not_really',
      label: t('performanceSnapshot.strengthsSkills.status.notReally'),
      Icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-600',
    },
    {
      value: 'not_sure',
      label: t('performanceSnapshot.strengthsSkills.status.notSure'),
      Icon: HelpCircle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-600',
    },
  ];

  const handleStatusChange = (itemId: string, status: string) => {
    const updated = items.map((item) =>
      item.id === itemId ? { ...item, status } : item,
    );
    setItems(updated);
    onStrengthsValidationUpdate?.({
      validations: updated.map((item) => ({
        name: item.name,
        type: item.type,
        status: item.status || 'pending',
      })),
    });
  };

  const renderItem = (item: ReviewItem) => {
    const selectedOption = statusOptions.find(
      (opt) => opt.value === item.status,
    );

    return (
      <div
        key={item.id}
        className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="font-semibold text-foreground text-base">
              {item.name}
            </h4>
            <div className="flex items-center gap-3 mt-1">
              {item.proficiency && (
                <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">
                  {item.proficiency}
                </span>
              )}
              {item.endorsements > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t('performanceSnapshot.strengthsSkills.endorsements', {
                    count: item.endorsements,
                  })}
                </span>
              )}
            </div>
          </div>
          {selectedOption && (
            <div
              className={`flex items-center space-x-1 px-2 py-1 rounded-md ${selectedOption.bgColor}`}
            >
              <selectedOption.Icon
                size={14}
                className={selectedOption.color}
              />
              <span
                className={`text-xs font-medium ${selectedOption.color}`}
              >
                {selectedOption.label}
              </span>
            </div>
          )}
        </div>
        <div className="flex space-x-2">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(item.id, option.value)}
              className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-md border-2 transition-all duration-200 ${
                item.status === option.value
                  ? `${option.borderColor} ${option.bgColor}`
                  : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <option.Icon
                size={16}
                className={
                  item.status === option.value
                    ? option.color
                    : 'text-muted-foreground'
                }
              />
              <span
                className={`text-xs font-medium ${
                  item.status === option.value
                    ? option.color
                    : 'text-muted-foreground'
                }`}
              >
                {option.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Empty state when no skills data exists
  if (items.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Award size={48} className="mx-auto mb-4 opacity-50" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('performanceSnapshot.strengthsSkills.noData')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('performanceSnapshot.strengthsSkills.noDataDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/10 via-indigo-500/10 to-purple-500/10 border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <Award size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">
              {t('performanceSnapshot.strengthsSkills.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('performanceSnapshot.strengthsSkills.subtitle', {
                name: employee.name,
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Skills Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Sparkles size={20} className="text-purple-600" />
          <h3 className="text-lg font-semibold text-foreground">
            {t('performanceSnapshot.strengthsSkills.strengthsLabel')}
          </h3>
          <span className="text-sm text-muted-foreground">
            ({items.filter((s) => s.status).length}/{items.length}{' '}
            {t('performanceSnapshot.strengthsSkills.reviewed')})
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item) => renderItem(item))}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {t('performanceSnapshot.strengthsSkills.reviewProgress')}:
          </span>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <CheckCircle size={16} className="text-green-600" />
              <span className="font-medium text-foreground">
                {items.filter((s) => s.status === 'accurate').length}{' '}
                {t('performanceSnapshot.strengthsSkills.status.accurate')}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <XCircle size={16} className="text-red-600" />
              <span className="font-medium text-foreground">
                {items.filter((s) => s.status === 'not_really').length}{' '}
                {t('performanceSnapshot.strengthsSkills.status.notReally')}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <HelpCircle size={16} className="text-amber-600" />
              <span className="font-medium text-foreground">
                {items.filter((s) => s.status === 'not_sure').length}{' '}
                {t('performanceSnapshot.strengthsSkills.status.notSure')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurfedStrengthsSkillsPanel;
